import { useState, useEffect, useCallback } from "react";
import { Send, Plus, CheckSquare, Square } from "lucide-react";
import { api, fmt } from "../../lib/api";
import { toast } from "sonner";

/* ----------------- MONTHLY SUMMARY ----------------- */
export function Summary() {
  const [m1, setM1] = useState(""); const [m2, setM2] = useState(""); const [m3, setM3] = useState("");
  const [i1, setI1] = useState(""); const [i2, setI2] = useState(""); const [i3, setI3] = useState("");
  const [resource, setResource] = useState("");
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/monthly-summary").then((r) => setList(r.data.summaries));
  useEffect(() => { load(); }, []);
  const submit = async (sendNow) => {
    const matters = [m1, m2, m3].filter((s) => s.trim()); const ignore = [i1, i2, i3].filter((s) => s.trim());
    if (matters.length === 0 || !resource.trim()) { toast.error("Need at least 1 bullet and 1 resource"); return; }
    await api.post("/admin/monthly-summary", { matters, ignore, one_resource: resource, send_now: sendNow });
    toast.success(sendNow ? "Sent to all members" : "Saved");
    setM1(""); setM2(""); setM3(""); setI1(""); setI2(""); setI3(""); setResource("");
    load();
  };
  return (
    <div>
      <div className="overline mb-3">// MONTHLY EXECUTIVE SUMMARY</div>
      <div className="panel p-6 space-y-4">
        <div><label className="overline">// 3 BULLETS &middot; WHAT MATTERS</label>
          <input value={m1} onChange={(e) => setM1(e.target.value)} placeholder="Bullet 1" />
          <input value={m2} onChange={(e) => setM2(e.target.value)} placeholder="Bullet 2" />
          <input value={m3} onChange={(e) => setM3(e.target.value)} placeholder="Bullet 3" /></div>
        <div><label className="overline">// WHAT TO IGNORE</label>
          <input value={i1} onChange={(e) => setI1(e.target.value)} placeholder="Ignore 1" />
          <input value={i2} onChange={(e) => setI2(e.target.value)} placeholder="Ignore 2" />
          <input value={i3} onChange={(e) => setI3(e.target.value)} placeholder="Ignore 3" /></div>
        <div><label className="overline">// THE ONE RESOURCE</label><input value={resource} onChange={(e) => setResource(e.target.value)} placeholder="The one resource they need this month" /></div>
        <div className="flex gap-3">
          <button onClick={() => submit(true)} className="btn-gold"><Send className="w-4 h-4"/> Send to all members</button>
          <button onClick={() => submit(false)} className="btn-ghost">Save as draft</button>
        </div>
      </div>
      <h3 className="font-display text-2xl text-cream mt-10 mb-4">Sent / scheduled</h3>
      <div className="space-y-2">{list.map((s) => (<div key={s.id} className="panel p-4"><div className="overline">{s.sent ? `SENT ${fmt.datetime(s.sent_at)}` : "DRAFT"}</div><div className="text-cream mt-2">{s.matters.join(" · ")}</div></div>))}</div>
    </div>
  );
}

/* ----------------- COMMUNITY ADMIN ----------------- */
export function CommunityAdmin() {
  const [manifesto, setManifesto] = useState("");
  const [rules, setRules] = useState("");
  useEffect(() => {
    api.get("/admin/settings/manifesto").then((r) => setManifesto(r.data.value?.body_md || ""));
    api.get("/admin/settings/community_rules").then((r) => setRules(r.data.value?.body_md || ""));
  }, []);
  return (
    <div className="space-y-8">
      <div className="panel p-6">
        <div className="overline mb-3">// ONE PAGE MANIFESTO</div>
        <textarea rows={10} value={manifesto} onChange={(e) => setManifesto(e.target.value)} />
        <button onClick={async () => { await api.post("/admin/manifesto", { body_md: manifesto }); toast.success("Manifesto saved"); }} className="btn-gold mt-4">Save manifesto</button>
      </div>
      <div className="panel p-6">
        <div className="overline mb-3">// HOUSE RULES</div>
        <textarea rows={8} value={rules} onChange={(e) => setRules(e.target.value)} />
        <button onClick={async () => { await api.post("/admin/community-rules", { body_md: rules }); toast.success("Rules saved"); }} className="btn-gold mt-4">Save rules</button>
      </div>
    </div>
  );
}

/* ----------------- REMINDERS ----------------- */
export function Reminders() {
  const [data, setData] = useState(null);
  const [title, setTitle] = useState(""); const [when, setWhen] = useState("");
  const load = () => api.get("/admin/reminders").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const logPost = async (kind) => { await api.post("/admin/reminders/log-post", { kind }); load(); };
  const add = async (e) => { e.preventDefault(); await api.post("/admin/reminders", { title, when: when ? new Date(when).toISOString() : null }); setTitle(""); setWhen(""); load(); };
  const toggle = async (id, done) => { await api.patch(`/admin/reminders/${id}`, { completed: !done }); load(); };
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-px bg-borderGold mb-8">
        <div className="bg-void p-6">
          <div className="overline mb-3">// TODAY'S SALES POSTS</div>
          <div className="font-display text-5xl text-cream">{data.today.sales_posts_done} / {data.today.sales_posts_target}</div>
          <p className="text-textMuted text-sm mt-2">Post 2 sales angles per day. Tap below each time.</p>
          <button onClick={() => logPost("sales")} className="btn-gold mt-4"><Plus className="w-4 h-4"/> Log sales post</button>
        </div>
        <div className="bg-void p-6">
          <div className="overline mb-3">// TODAY'S NURTURE POSTS</div>
          <div className="font-display text-5xl text-cream">{data.today.nurture_posts_done} / {data.today.nurture_posts_target}</div>
          <p className="text-textMuted text-sm mt-2">2 free nurturing posts into the funnel group per day.</p>
          <button onClick={() => logPost("nurture")} className="btn-ghost mt-4"><Plus className="w-4 h-4"/> Log nurture post</button>
        </div>
      </div>
      <form onSubmit={add} className="flex flex-wrap gap-3 mb-6">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Reminder title" className="flex-1 min-w-[260px]"/>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="max-w-[220px]"/>
        <button type="submit" className="btn-gold"><Plus className="w-4 h-4"/> Add</button>
      </form>
      <div className="space-y-2">
        {data.items.map((r) => (
          <button key={r.id} onClick={() => toggle(r.id, r.completed)} className={`block w-full text-left panel p-4 ${r.completed ? "opacity-50" : ""}`}>
            <div className="flex items-center gap-3">{r.completed ? <CheckSquare className="w-5 h-5 text-gold"/> : <Square className="w-5 h-5 text-textMuted"/>}
              <div><div className={`${r.completed ? "line-through text-textMuted" : "text-cream"}`}>{r.title}</div>{r.when && <div className="text-xs font-mono text-textDim">{fmt.datetime(r.when)}</div>}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ----------------- EMAIL TEMPLATES (preview + test send) ----------------- */
export function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => api.get("/admin/email-templates").then((r) => {
    setTemplates(r.data.templates);
    setSelected((current) => current || (r.data.templates.length ? r.data.templates[0] : null));
  }), []);
  useEffect(() => { load(); }, [load]);
  const sendTest = async (key) => {
    const r = await api.post(`/admin/email-templates/${key}/send-test`);
    toast.success(`Test email ${r.data.status}`);
    load();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-px bg-borderGold">
      <div className="bg-void p-4 lg:col-span-1">
        <div className="overline mb-3">// {templates.length} PRE-WRITTEN TEMPLATES</div>
        <div className="space-y-1">
          {templates.map((t) => (
            <button key={t.key} data-testid={`email-tpl-${t.key}`} onClick={() => setSelected(t)} className={`w-full text-left p-3 border-l-2 ${selected?.key === t.key ? "border-gold bg-surface" : "border-transparent hover:border-borderGoldHi"}`}>
              <div className="text-cream text-sm">{t.label}</div>
              <div className="text-xs font-mono text-textDim mt-1">{t.key}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-void p-6 lg:col-span-2">
        {selected ? (
          <>
            <div className="overline mb-2">// {selected.key}</div>
            <h3 className="font-display text-3xl text-cream">{selected.label}</h3>
            <p className="text-textMuted text-sm mt-2">{selected.trigger}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button data-testid={`email-test-${selected.key}`} onClick={() => sendTest(selected.key)} className="btn-gold"><Send className="w-4 h-4"/> Send test to me</button>
              {selected.variables?.length > 0 && <div className="overline self-center">VARS: {selected.variables.join(", ")}</div>}
            </div>
            <div className="overline mt-8 mb-2">// SUBJECT PREVIEW</div>
            <div className="panel p-3 text-cream">{selected.preview_subject}</div>
            <div className="overline mt-6 mb-2">// HTML PREVIEW</div>
            <iframe srcDoc={selected.preview_html} title="preview" className="w-full h-[600px] bg-cream"/>
          </>
        ) : <p className="text-textMuted">Pick a template.</p>}
      </div>
    </div>
  );
}

/* ----------------- OUTBOX ----------------- */
export function Outbox() {
  const [emails, setEmails] = useState([]);
  useEffect(() => { api.get("/admin/outbox").then((r) => setEmails(r.data.emails)); }, []);
  return (
    <div>
      <div className="overline mb-3">// EMAIL LOG &middot; everything queued or sent</div>
      <div className="space-y-2">
        {emails.map((e) => (
          <div key={e.id} className="panel p-4">
            <div className="flex items-center justify-between"><div><div className="text-cream">{e.subject}</div><div className="text-xs font-mono text-textDim">{e.to} &middot; {e.kind}</div></div><span className="overline">{e.status}</span></div>
          </div>
        ))}
        {emails.length === 0 && <p className="text-textMuted">No emails yet.</p>}
      </div>
    </div>
  );
}
